import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRightLeft, Check } from "lucide-react";
import DashboardPanel from "./DashboardPanel";
import DeleteModal from "../models/deleteModal.jsx";
import { convertExpertRequestToWarm } from "../../controllers/websiteController";

const timeAgo = (value) => {
  const t = new Date(value).getTime();
  if (Number.isNaN(t)) return "";
  const minutes = Math.max(0, Math.floor((Date.now() - t) / 60000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const joinParts = (...parts) => parts.filter(Boolean).join(" · ");

function SubHeading({ children, to }) {
  return (
    <div className="mb-1 flex items-center justify-between">
      <h4 className="text-[11px] font-semibold uppercase tracking-wider text-app-muted">{children}</h4>
      <Link to={to} className="text-[11px] font-semibold text-blue-600 hover:text-blue-700 dark:text-sky-400 dark:hover:text-sky-300">
        View all
      </Link>
    </div>
  );
}

function ExpertRow({ request, onConvert }) {
  return (
    <li className="flex items-center gap-3 py-2.5">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-app-primary">{request.fullName || "—"}</p>
        <p className="truncate text-xs text-app-muted">
          {joinParts(request.jobTitle, request.companyName, request.country, timeAgo(request.createdAt))}
        </p>
      </div>
      {request.convertedToWarm ? (
        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-700 dark:bg-green-500/15 dark:text-green-300">
          <Check size={12} /> Converted
        </span>
      ) : (
        <button
          type="button"
          onClick={() => onConvert(request)}
          className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-app-primary bg-app-card px-2.5 text-xs font-medium text-app-secondary transition-colors hover:bg-app-hover hover:text-app-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
        >
          <ArrowRightLeft size={13} strokeWidth={1.75} aria-hidden="true" />
          Convert to Warm
        </button>
      )}
    </li>
  );
}

function ContactRow({ query }) {
  return (
    <li className="py-2.5">
      <div className="flex items-center justify-between gap-2">
        <p className="truncate text-sm font-medium text-app-primary">{query.name || "—"}</p>
        <span className="shrink-0 text-xs text-app-muted">{timeAgo(query.createdAt)}</span>
      </div>
      <p className="truncate text-xs text-app-muted">
        {joinParts(query.inquiryType, query.businessName)}
      </p>
    </li>
  );
}

function Skeleton({ rows }) {
  return (
    <div className="space-y-3" aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="space-y-1.5">
          <div className="h-4 w-1/2 animate-pulse rounded bg-gray-200 dark:bg-slate-700" />
          <div className="h-3 w-3/4 animate-pulse rounded bg-gray-100 dark:bg-slate-700/60" />
        </div>
      ))}
    </div>
  );
}

function Section({ loading, error, rows, emptyText, skeletonRows, children }) {
  if (loading) return <Skeleton rows={skeletonRows} />;
  if (error) return <p className="py-2 text-sm text-app-error">{"Couldn't load."}</p>;
  if (!rows?.length) return <p className="py-2 text-sm text-app-muted">{emptyText}</p>;
  return <ul className="divide-y divide-gray-100 dark:divide-slate-700">{children}</ul>;
}

/**
 * Newest expert requests (with an inline convert-to-warm action) and contact
 * queries from the public website. `onConverted(id)` fires after a successful
 * conversion so the parent can update its data.
 */
function WebsiteLeadsCard({ experts, contacts, loading = false, onConverted }) {
  const [toConvert, setToConvert] = useState(null);
  const [converting, setConverting] = useState(false);
  const [convertError, setConvertError] = useState(null);

  const closeModal = () => {
    if (converting) return;
    setToConvert(null);
  };

  const handleConfirm = async () => {
    if (!toConvert?.id) return;
    try {
      setConverting(true);
      setConvertError(null);
      await convertExpertRequestToWarm(toConvert.id);
      onConverted?.(toConvert.id);
      setToConvert(null);
    } catch {
      setConvertError(`Couldn't convert "${toConvert.fullName || "request"}". Please try again.`);
      setToConvert(null);
    } finally {
      setConverting(false);
    }
  };

  return (
    <DashboardPanel title="Website Leads" description="Latest submissions from the public website.">
      <div className="space-y-5">
        <div>
          <SubHeading to="/admin/expert-requests">Expert requests</SubHeading>
          {convertError && (
            <p role="alert" className="mb-1 rounded-md bg-app-error px-2.5 py-1.5 text-xs text-app-error">
              {convertError}
            </p>
          )}
          <Section
            loading={loading}
            error={Boolean(experts?.error)}
            rows={experts?.data?.latest}
            emptyText="No expert requests yet."
            skeletonRows={3}
          >
            {experts?.data?.latest.map((r) => (
              <ExpertRow key={r.id} request={r} onConvert={setToConvert} />
            ))}
          </Section>
        </div>

        <div>
          <SubHeading to="/admin/contact-queries">Contact queries</SubHeading>
          <Section
            loading={loading}
            error={Boolean(contacts?.error)}
            rows={contacts?.data?.latest}
            emptyText="No contact queries yet."
            skeletonRows={3}
          >
            {contacts?.data?.latest.map((q) => (
              <ContactRow key={q.id} query={q} />
            ))}
          </Section>
        </div>
      </div>

      <DeleteModal
        show={Boolean(toConvert)}
        handleClose={closeModal}
        handleConfirm={handleConfirm}
        loading={converting}
        title="Convert to Warm Record"
        confirmText="Convert"
        confirmingText="Converting..."
        confirmButtonClassName="bg-[#2470B7] hover:bg-[#1d5a93]"
        message={`Convert "${toConvert?.fullName || "this request"}" into a warm database record?`}
      />
    </DashboardPanel>
  );
}

export default WebsiteLeadsCard;
